import { useState } from 'react';
import {
  Box, Heading, HStack, Switch, Text, VStack,
} from '@gluestack-ui/themed';

import {
  BriefingHandIndicator,
  BriefingStudentAudioButton,
  BriefingStudentMutedIndicator,
} from '@/common/components/oralExam/BriefingParticipantControls';
import { LabStandInImage } from '@/common/oralExam/oralExamLabOverrides';
import { PATROL_LAB_STANDINS } from '@/visual-lab/chapters/patrol/patrolLabStandIns';

type BriefingLabStudent = {
  identity: string;
  name: string;
  portrait: (typeof PATROL_LAB_STANDINS)['harry'];
  muted: boolean;
  handRaised: boolean;
};

const INITIAL_STUDENTS: BriefingLabStudent[] = [
  {
    identity: 'student-harry',
    name: 'Harry Potter',
    portrait: PATROL_LAB_STANDINS.harry,
    muted: true,
    handRaised: false,
  },
  {
    identity: 'student-hermione',
    name: 'Hermione Granger',
    portrait: PATROL_LAB_STANDINS.hermione,
    muted: true,
    handRaised: true,
  },
  {
    identity: 'student-ron',
    name: 'Ron Weasley',
    portrait: PATROL_LAB_STANDINS.ron,
    muted: true,
    handRaised: false,
  },
];

/**
 * Group-briefing commissioning surface using the production hand and microphone controls.
 */
export function GroupBriefingLabScreen() {
  const [students, setStudents] = useState(INITIAL_STUDENTS);
  const [muteAll, setMuteAll] = useState(true);

  const updateStudent = (
    identity: string,
    update: (student: BriefingLabStudent) => BriefingLabStudent,
  ) => {
    setStudents((current) => current.map(
      (student) => (student.identity === identity ? update(student) : student),
    ));
  };

  const handleMuteAll = (enabled: boolean) => {
    setMuteAll(enabled);
    if (enabled) {
      setStudents((current) => current.map((student) => ({ ...student, muted: true })));
    }
  };

  const handleStudentMuted = (identity: string, muted: boolean) => {
    updateStudent(identity, (student) => ({ ...student, muted }));
    if (!muted) setMuteAll(false);
  };

  return (
    <VStack
      flex={1}
      minHeight={0}
      space="lg"
      px="$6"
      py="$5"
      testID="group-briefing-lab"
    >
      <HStack alignItems="flex-start" justifyContent="space-between" space="lg">
        <VStack space="xs" flex={1}>
          <Heading size="xl">Group briefing controls</Heading>
          <Text size="sm" color="$textSubtle">
            Production tile controls. Toggle hands and microphones to inspect every state.
          </Text>
        </VStack>
        <HStack
          alignItems="center"
          space="sm"
          bg="$bgCard"
          borderWidth={1}
          borderColor="$borderLight"
          borderRadius="$full"
          px="$3"
          py="$2"
        >
          <Switch
            value={muteAll}
            onValueChange={handleMuteAll}
            accessibilityLabel="Mute all"
            testID="group-briefing-lab-mute-all"
          />
          <Text size="sm" fontFamily="$heading">Mute all</Text>
        </HStack>
      </HStack>

      <HStack alignItems="flex-start" space="lg" flexWrap="wrap">
        {students.map((student) => (
          <VStack
            key={student.identity}
            width={240}
            space="sm"
            testID={`group-briefing-lab-student-${student.identity}`}
          >
            <Box
              width="100%"
              height={150}
              borderRadius="$lg"
              overflow="hidden"
              borderWidth={1}
              borderColor="$borderLight"
              bg="$bgDark"
              position="relative"
            >
              <LabStandInImage source={student.portrait} />
              <Box position="absolute" top="$1" right="$1" zIndex={2} pointerEvents="none">
                <BriefingHandIndicator
                  identity={student.identity}
                  raised={student.handRaised}
                />
              </Box>
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                pointerEvents="box-none"
              >
                <BriefingStudentMutedIndicator
                  identity={student.identity}
                  muted={student.muted}
                />
              </Box>
            </Box>
            <Text size="sm" fontFamily="$heading">{student.name}</Text>
            <HStack alignItems="center" space="xs">
              <BriefingStudentAudioButton
                identity={student.identity}
                muted={student.muted}
                onToggle={(muted) => handleStudentMuted(student.identity, muted)}
              />
              <BriefingHandIndicator
                identity={`lab-${student.identity}`}
                raised={student.handRaised}
                interactive
                onToggle={(raised) => updateStudent(
                  student.identity,
                  (current) => ({ ...current, handRaised: raised }),
                )}
              />
            </HStack>
          </VStack>
        ))}
      </HStack>
    </VStack>
  );
}
